import { useEffect, useRef, useState, type ReactNode } from "react";
import { PRO_TIP } from "../lib/edition";
import { hint, type MenuItem } from "../menus";

/** What decides whether a menu item can be used: who you are, where you are, what the page implements, your edition. */
export interface MenuState {
  admin: boolean;
  /** False for a viewer. Optional so existing callers keep their behaviour. */
  canEdit?: boolean;
  annotateView: boolean;
  hasProject: boolean;
  implemented: Set<string>;
  /** Ticks for checkable items (Depth View), by action id. */
  checked?: Record<string, boolean>;
  /** Whether a licensed feature is open; absent = all open. */
  has?: (feature: string) => boolean;
}

export const featureClosed = (item: MenuItem, s: MenuState) => !!item.feature && !!s.has && !s.has(item.feature);

export function itemEnabled(item: MenuItem, s: MenuState): boolean {
  if (!item.action || !s.implemented.has(item.action)) return false;
  if (item.adminOnly && !s.admin) return false;
  if (item.editOnly && s.canEdit === false) return false;
  if (item.annotateOnly && !s.annotateView) return false;
  if (item.needsProject && !s.hasProject) return false;
  if (featureClosed(item, s)) return false;
  return true;
}

/**
 * A button that opens a list of actions from menus.ts (the catalogue that replaced the desktop's menu bar).
 * Admin-only items are left out for everyone else; anything else that cannot be used now is shown disabled with the
 * reason as its tooltip. Esc or a click elsewhere closes it; arrow keys move between items.
 */
export function ActionMenu({ label, items, state, onAction, testId, className = "btn", align = "left", header, title, drop = "down" }: {
  label: ReactNode; items: MenuItem[]; state: MenuState; onAction: (action: string) => void; testId: string;
  className?: string; align?: "left" | "right"; header?: ReactNode; title?: string; drop?: "down" | "right";
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    root.current?.querySelector<HTMLButtonElement>('[role="menu"] button:not(:disabled)')?.focus();
    const away = (e: MouseEvent) => { if (!root.current?.contains(e.target as Node)) setOpen(false); };
    const keys = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); setOpen(false); return; }
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      const buttons = [...(root.current?.querySelectorAll<HTMLButtonElement>('[role="menu"] button:not(:disabled)') ?? [])];
      if (!buttons.length) return;
      e.preventDefault(); e.stopPropagation();
      const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
      buttons[(at + (e.key === "ArrowDown" ? 1 : -1) + buttons.length) % buttons.length].focus();
    };
    window.addEventListener("mousedown", away);
    window.addEventListener("keydown", keys, true);
    return () => { window.removeEventListener("mousedown", away); window.removeEventListener("keydown", keys, true); };
  }, [open]);

  const shown = items.filter((it) => it.separator || !(it.adminOnly && !state.admin));
  // No separator first, last, or twice in a row once hidden items are gone.
  const tidy = shown.filter((it, i) => !it.separator || (i > 0 && i < shown.length - 1 && !shown[i - 1].separator));
  const why = (it: MenuItem) => !it.action || !state.implemented.has(it.action) ? "Not available on this page"
    : featureClosed(it, state) ? PRO_TIP
    : it.editOnly && state.canEdit === false ? "Your role can look at projects but not change them"
    : it.annotateOnly && !state.annotateView ? "Open an image to annotate first" : undefined;

  return (
    <div className="action-menu" ref={root}>
      <button className={className} aria-haspopup="menu" aria-expanded={open} data-testid={testId} title={title} aria-label={title}
        onClick={() => setOpen(!open)}>{label}</button>
      {open && (
        <div className={`menu menu-${align} menu-drop-${drop}`} role="menu">
          {header}
          {tidy.map((it, k) => it.separator ? <div key={k} className="menu-sep" role="separator" /> : (() => {
            const enabled = itemEnabled(it, state);
            return (
              <button key={it.label} className="menu-item" role={it.checkable ? "menuitemcheckbox" : "menuitem"} disabled={!enabled}
                aria-checked={it.checkable ? !!state.checked?.[it.action ?? ""] : undefined}
                title={enabled ? undefined : why(it)} data-testid={`item-${it.action ?? it.label}`}
                onClick={() => { setOpen(false); if (it.action) onAction(it.action); }}>
                {it.checkable && <span className="tick" aria-hidden="true">{state.checked?.[it.action ?? ""] ? "✓" : ""}</span>}
                <span>{it.display ?? it.label}{featureClosed(it, state) && <span className="pill pro" style={{ marginLeft: 8 }}>Pro</span>}</span>
                <span className="key">{it.shortcut ? hint(it.shortcut) : ""}</span>
              </button>
            );
          })())}
        </div>
      )}
    </div>
  );
}
